import React, { useEffect, useState } from 'react';
import { TEST_DIFFICULTY_OPTIONS } from '../../../constants/testDifficulty';
import { fetchSubgroups, humanError, updateTest } from '../../api';
import type { TestPatch } from '../../api';
import { BLOCK_LABEL, MINUTE_CHOICES, OverviewDetail, PASS_PERCENT } from '../../model';
import type { WorkspaceNotice } from '../../notices';
import { minutesLabel } from '../../words';


interface Props {
  detail: OverviewDetail;
  reload: () => Promise<void>;
  notify: (notice: WorkspaceNotice) => void;
}

export const SettingsTab: React.FC<Props> = ({ detail, reload, notify }) => {
  const [title, setTitle] = useState(detail.title);
  const [description, setDescription] = useState(detail.description ?? '');
  const [minutes, setMinutes] = useState(detail.time_limit_minutes);
  const [subgroup, setSubgroup] = useState(detail.subgroup ?? '');
  const [difficulty, setDifficulty] = useState(detail.difficulty ?? '');
  const [passingScore, setPassingScore] = useState(String(detail.passing_score ?? PASS_PERCENT));
  const [subgroups, setSubgroups] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchSubgroups()
      .then((list) => { if (!cancelled) setSubgroups(list); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const options = detail.subgroup && !subgroups.includes(detail.subgroup)
    ? [detail.subgroup, ...subgroups]
    : subgroups;

  const save = async () => {
    if (percentInvalid || saving) return;
    const patch: TestPatch = {};
    if (title.trim() !== detail.title) patch.title = title.trim();
    if (description !== (detail.description ?? '')) patch.description = description;
    if (minutes !== detail.time_limit_minutes) patch.time_limit_minutes = minutes;
    if (subgroup !== (detail.subgroup ?? '')) patch.subgroup = subgroup;
    if (difficulty !== (detail.difficulty ?? '')) patch.difficulty = difficulty || null;
    if (Number(passingScore) !== (detail.passing_score ?? PASS_PERCENT)) patch.passing_score = Number(passingScore);
    setSaving(true);
    try {
      if (Object.keys(patch).length > 0) await updateTest(detail.id, patch);
      notify({ kind: 'ok', text: 'Настройки сохранены.' });
      await reload();
    } catch (error) {
      notify({ kind: 'info', text: humanError(error, 'Не получилось сохранить настройки. Попробуйте ещё раз.') });
    } finally {
      setSaving(false);
    }
  };

  const titleEmpty = title.trim() === '';
  const percentInvalid = passingScore.trim() === '' || !Number.isInteger(Number(passingScore)) || Number(passingScore) < 1 || Number(passingScore) > 100;
  return (
    <div className="tw-card tw-stack-sm">
      <h2>Настройки теста</h2>
      <div>
        <label className="tw-label" htmlFor="tw-s-title">Название теста</label>
        <input id="tw-s-title" className="tw-field" type="text" maxLength={255} value={title} onChange={(event) => setTitle(event.target.value)} />
        {titleEmpty && <p className="tw-help" style={{ marginTop: 8 }}>Впишите название теста.</p>}
      </div>
      <div>
        <label className="tw-label" htmlFor="tw-s-description">Пояснение для сотрудников (необязательно)</label>
        <input
          id="tw-s-description"
          className="tw-field"
          type="text"
          maxLength={5000}
          placeholder="Например: по материалам октябрьского обучения"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>
      <div>
        <div className="tw-label">Сколько минут дать на тест?</div>
        <div className="tw-row" role="group" aria-label="Время на тест">
          {MINUTE_CHOICES.map((value) => (
            <button
              key={value}
              type="button"
              className="tw-chip tw-chip-wide"
              aria-pressed={minutes === value}
              onClick={() => setMinutes(value)}
            >
              {`${value} мин`}
            </button>
          ))}
        </div>
        {!MINUTE_CHOICES.includes(minutes) && (
          <p className="tw-help" style={{ marginTop: 10 }}>{`Сейчас: ${minutesLabel(minutes)}.`}</p>
        )}
      </div>
      <div>
        <label className="tw-label" htmlFor="tw-s-passing-score">Сколько процентов верных ответов нужно, чтобы сдать?</label>
        <input id="tw-s-passing-score" className="tw-field" type="number" min={1} max={100} step={1} value={passingScore}
          aria-invalid={percentInvalid} aria-describedby="tw-s-passing-help" onChange={(event) => setPassingScore(event.target.value)} />
        <p id="tw-s-passing-help" className="tw-help">{percentInvalid ? 'Укажите целое число от 1 до 100.' : 'Проходной процент для этого теста: от 1 до 100.'}</p>
        {detail.attempts.completed > 0 && <p className="tw-help">При изменении порога статус «Сдал / Не сдал» у завершённых попыток обновится. Проценты результатов сохранятся.</p>}
      </div>
      <div>
        <label className="tw-label" htmlFor="tw-s-difficulty">Сложность теста</label>
        <select id="tw-s-difficulty" className="tw-field" value={difficulty} onChange={(event) => setDifficulty(event.target.value)}>
          <option value="">Не задана</option>
          {TEST_DIFFICULTY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>
      <div>
        <div className="tw-label">Раздел</div>
        <p className="tw-help">{BLOCK_LABEL[detail.block]}</p>
        <p className="tw-help">Менять нельзя.</p>
      </div>
      <div>
        <label className="tw-label" htmlFor="tw-s-subgroup">Тема (необязательно)</label>
        <select id="tw-s-subgroup" className="tw-field" value={subgroup} onChange={(event) => setSubgroup(event.target.value)}>
          <option value="">Без темы</option>
          {options.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
      </div>
      <div className="tw-row tw-btn-row">
        <button type="button" className="tw-btn tw-btn-primary" disabled={titleEmpty || percentInvalid || saving} onClick={() => void save()}>
          Сохранить изменения
        </button>
      </div>
    </div>
  );
};
